import { canReadDomain } from '@/api/core/authorization';
import {
  readPropertyMediaGroups,
  readPropertyMediaItems,
  type MediaTarget,
} from '@/api/core/property-media';
import {
  decodeMediaSelectionCreated,
  validateMediaItemReceipt,
  validateMediaReceipt,
} from '@/api/core/property-media-edit';
import type { CreateMediaCollection, MediaWriteReceipt } from '@/api/core/property-media-write';
import {
  createMediaUrl,
  uploadManagedImage,
  type CreateMediaUrl,
  type UploadManagedImage,
} from '@/api/core/property-media-write';
import { rawEnvelope } from '@/api/http/envelopes';
import { isApiError } from '@/api/http/errors';
import type { OperatorSession } from '@/auth/session';
import { safeImageUri } from '@/lib/safe-image-uri';
import { isUuid } from '@/lib/validation';
import { Button } from '@mgrvtechgroup/admin-ds';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { freezeRequest } from '../presentation/command-owner';
import type { PropertyRuntime } from '../property-queries';
import type { EditPending } from './media-edit-form';

export function canManageMedia(runtime: PropertyRuntime, generation: number) {
  const state = runtime.session.getSnapshot();
  return (
    state.status === 'authorized' &&
    state.generation === generation &&
    !!state.authorization &&
    !state.deniedDomains.includes('property') &&
    canReadDomain(state.authorization, 'property', 4)
  );
}
export async function verifyMediaActor(
  runtime: PropertyRuntime,
  generation: number,
): Promise<string | null> {
  if (!canManageMedia(runtime, generation)) return null;
  try {
    const value = await runtime.client.get<unknown>('users/me');
    if (!canManageMedia(runtime, generation) || !value || typeof value !== 'object') return null;
    const id = (value as { user_id?: unknown }).user_id;
    return isUuid(id) ? id : null;
  } catch {
    return null;
  }
}

export type Attempt =
  | { kind: 'file'; value: UploadManagedImage; actorId?: string }
  | { kind: 'url'; value: CreateMediaUrl; collectionId: string; actorId?: string };
interface CreateAttempt {
  body: CreateMediaCollection;
  actorId?: string;
}
type Command = Attempt | CreateAttempt | EditPending;
interface Lease {
  command: Command;
  sent: boolean;
}
interface OwnerSnapshot {
  editAttempt: EditPending | null;
  progress: string;
  attempt: Attempt | null;
  createAttempt: CreateAttempt | null;
}
interface Source {
  version: number;
  snapshot: OwnerSnapshot;
  listeners: Set<() => void>;
  epoch: string;
  flight: Lease | null;
  originalEdit?: {
    command: EditPending;
    stage: string;
    actorId?: string;
    namespace: string;
    client: PropertyRuntime['client'];
    epoch: string;
    path: string;
    body: object;
    json: string;
    sent: boolean;
  };
}
interface WriteOwner extends OwnerSnapshot {
  epoch: () => number;
  checking: (lease: Lease) => void;
  claim: (command: Command) => Lease | null;
  current: (lease: Lease) => boolean;
  bind: (lease: Lease, actorId: string) => boolean;
  clear: (lease: Lease) => void;
  release: (lease: Lease) => void;
  replayEdit: (lease: Lease) => Promise<void>;
  replayableEdit: (lease: Lease) => boolean;
  rejectEdit: (lease: Lease) => void;
}
const WriteContext = createContext<WriteOwner | null>(null);
const emptyOwner: OwnerSnapshot = {
  attempt: null,
  createAttempt: null,
  editAttempt: null,
  progress: '',
};
const owners = new WeakMap<OperatorSession, Map<string, Source>>();
function publishOwner(source: Source, snapshot: OwnerSnapshot) {
  source.snapshot = { ...snapshot };
  source.listeners.forEach((listener) => {
    listener();
  });
}
function sessionEpoch(runtime: PropertyRuntime) {
  const s = runtime.session.getSnapshot();
  return JSON.stringify([s.status, s.generation, s.authorization, s.deniedDomains]);
}
export function MediaWriteOwnerProvider({
  children,
  runtime,
  propertyId,
}: {
  children: ReactNode;
  runtime: PropertyRuntime;
  propertyId: string;
}) {
  let byProperty = owners.get(runtime.session);
  if (!byProperty) {
    byProperty = new Map();
    owners.set(runtime.session, byProperty);
  }
  let owner = byProperty.get(propertyId);
  if (!owner) {
    owner = {
      version: 0,
      snapshot: emptyOwner,
      listeners: new Set(),
      epoch: sessionEpoch(runtime),
      flight: null,
    };
    byProperty.set(propertyId, owner);
    const watched = owner;
    // Keep watching while unmounted: revocation followed by restoration must not revive a command.
    runtime.session.subscribe(() => {
      const epoch = sessionEpoch(runtime);
      if (epoch !== watched.epoch) {
        watched.version++;
        watched.epoch = epoch;
        watched.flight = null;
        publishOwner(watched, emptyOwner);
      }
    });
  }
  const source = owner;
  const snapshot = useSyncExternalStore(
    (listener) => {
      source.listeners.add(listener);
      return () => {
        source.listeners.delete(listener);
      };
    },
    () => source.snapshot,
  );
  const value = { ...snapshot, ...ownerActions(source, runtime) };
  return <WriteContext.Provider value={value}>{children}</WriteContext.Provider>;
}
function ownerActions(
  source: Source,
  runtime: PropertyRuntime,
): Omit<WriteOwner, keyof OwnerSnapshot> {
  const sync = () => {
    const epoch = sessionEpoch(runtime);
    if (epoch !== source.epoch) {
      source.version++;
      source.epoch = epoch;
      source.flight = null;
      publishOwner(source, emptyOwner);
    }
  };
  const current = (lease: Lease) => {
    sync();
    return (
      source.flight === lease && canManageMedia(runtime, runtime.session.getSnapshot().generation)
    );
  };
  const clear = (lease: Lease) => {
    if (!current(lease)) return;
    source.flight = null;
    source.originalEdit = undefined;
    publishOwner(source, emptyOwner);
  };
  return {
    epoch: () => source.version,
    current,
    clear,
    checking: (lease) => {
      if (current(lease))
        publishOwner(source, { ...source.snapshot, progress: '등록 결과 확인 중…' });
    },
    claim: (command) => {
      sync();
      const pending =
        source.snapshot.attempt ?? source.snapshot.createAttempt ?? source.snapshot.editAttempt;
      if (source.flight || (pending && pending !== command)) return null;
      const lease = { command, sent: pending === command };
      if ('edit' in command) {
        const request =
          command.stage === 'item' && command.itemBody
            ? {
                path: `admin/media-collections/${command.collectionId}/items/${command.itemId}`,
                body: command.itemBody,
              }
            : command.stage === 'selection' && command.selectionBody && command.assignment
              ? {
                  path: `admin/content-media-selections/${command.assignment}`,
                  body: command.selectionBody,
                }
              : command.stage === 'create-selection' && command.createBody
                ? { path: 'admin/content-media-selections', body: command.createBody }
                : null;
        if (request && source.originalEdit?.command !== command) {
          const retained =
            pending === command && Object.isFrozen(request.body) && !!command.actorId;
          freezeRequest(request.body);
          source.originalEdit = {
            command,
            stage: command.stage,
            actorId: command.actorId,
            namespace: runtime.apiNamespace,
            client: runtime.client,
            epoch: source.epoch,
            ...request,
            json: JSON.stringify(request.body),
            sent: retained,
          };
        }
      }
      source.flight = lease;
      publishOwner(
        source,
        'edit' in command
          ? { attempt: null, createAttempt: null, editAttempt: command, progress: '사진 변경 중…' }
          : 'body' in command
            ? {
                attempt: null,
                createAttempt: command,
                editAttempt: null,
                progress: '사진 그룹 등록 중…',
              }
            : {
                attempt: command,
                createAttempt: null,
                editAttempt: null,
                progress: '사진 등록 중…',
              },
      );
      return lease;
    },
    bind: (lease, actorId) => {
      if (!current(lease)) return false;
      if (lease.command.actorId && lease.command.actorId !== actorId) {
        clear(lease);
        return false;
      }
      if (source.originalEdit?.command === lease.command) {
        if (source.originalEdit.actorId && source.originalEdit.actorId !== actorId) return false;
        source.originalEdit.actorId = actorId;
      }
      lease.command.actorId = actorId;
      return true;
    },
    rejectEdit: (lease) => {
      if (current(lease) && source.originalEdit?.command === lease.command)
        source.originalEdit = undefined;
    },
    replayableEdit: (lease) =>
      current(lease) && source.originalEdit?.command === lease.command && source.originalEdit.sent,
    replayEdit: async (lease) => {
      const original = source.originalEdit;
      if (
        !current(lease) ||
        !original?.sent ||
        original.command !== lease.command ||
        source.snapshot.editAttempt !== lease.command ||
        original.namespace !== runtime.apiNamespace ||
        original.client !== runtime.client ||
        original.epoch !== source.epoch ||
        original.actorId !== lease.command.actorId ||
        !original.actorId
      )
        throw new Error('원래 사진 요청 소유자를 확인하지 못했습니다.');
      const command = original.command;
      const body =
        command.stage === 'item'
          ? command.itemBody
          : command.stage === 'selection'
            ? command.selectionBody
            : command.createBody;
      const path =
        command.stage === 'item'
          ? `admin/media-collections/${command.collectionId}/items/${command.itemId}`
          : command.stage === 'selection'
            ? `admin/content-media-selections/${command.assignment ?? ''}`
            : 'admin/content-media-selections';
      if (
        path !== original.path ||
        command.stage !== original.stage ||
        body !== original.body ||
        JSON.stringify(body) !== original.json
      )
        throw new Error('원래 사진 요청이 변경되었습니다.');
      if (command.stage === 'create-selection') {
        const value = await runtime.client.post<unknown>(original.path, {
          json: original.body,
          expectedStatus: 201,
        });
        command.assignment = decodeMediaSelectionCreated(
          value,
          (
            command.createBody ??
            (() => {
              throw new Error('원래 선택 요청 없음');
            })()
          ).command_id,
        );
      } else if (command.stage === 'item') {
        const value = await runtime.client.put<unknown>(original.path, {
          json: original.body,
          expectedStatus: 200,
          responseEnvelope: rawEnvelope,
        });
        if (!command.itemBody) throw new Error('원래 사진 요청 없음');
        validateMediaItemReceipt(value, command.collectionId, command.itemId, command.itemBody);
      } else {
        const value = await runtime.client.put<unknown>(original.path, {
          json: original.body,
          expectedStatus: 200,
        });
        if (!command.selectionBody || !command.assignment) throw new Error('원래 선택 요청 없음');
        validateMediaReceipt(value, command.selectionBody.command_id, command.assignment, null);
      }
    },
    release: (lease) => {
      if (!current(lease)) return;
      if (lease.sent) {
        if (source.originalEdit?.command === lease.command) source.originalEdit.sent = true;
        source.flight = null;
        publishOwner(source, { ...source.snapshot, progress: '' });
      } else clear(lease);
    },
  };
}
export function useMediaWriteOwner() {
  const owner = useContext(WriteContext);
  if (!owner) throw new Error('사진 명령 소유자가 없습니다.');
  return owner;
}
export function useMediaScope(owner: WriteOwner) {
  const mounted = useRef(false);
  const pending = useRef<Lease | null>(null);
  const release = useRef(owner.release);
  useEffect(() => {
    mounted.current = true;
    const cancel = release.current;
    return () => {
      mounted.current = false;
      if (pending.current && !pending.current.sent) cancel(pending.current);
    };
  }, []);
  return {
    mounted: () => mounted.current,
    track: (lease: Lease) => {
      pending.current = lease;
    },
  };
}
// Read the actual scoped response, including pagination, rather than trusting a cache reset.
export async function confirmMediaWrite(
  runtime: PropertyRuntime,
  propertyId: string,
  target: MediaTarget,
  receipt: MediaWriteReceipt,
  current: () => boolean,
) {
  if (!receipt.collectionId) throw new Error('Missing media collection');
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    if (!current()) throw new Error('Obsolete media command');
    if (receipt.itemId) {
      const page = await readPropertyMediaItems(
        runtime.client,
        propertyId,
        receipt.collectionId,
        { direction: 'asc', cursor },
        undefined,
        true,
      );
      if (
        !current() ||
        page.scope.targetKind !== target.kind ||
        page.scope.targetId !== target.id ||
        page.scope.floor !== target.floor ||
        page.group.id !== receipt.collectionId ||
        page.group.collectionRevision !== receipt.collectionRevision
      )
        throw new Error('Unconfirmed media scope/revision');
      if (page.items.some((item) => item.id === receipt.itemId)) return;
      cursor = page.page.nextCursor;
    } else {
      const page = await readPropertyMediaGroups(
        runtime.client,
        propertyId,
        target,
        { direction: 'asc', cursor },
        undefined,
        true,
      );
      if (!current()) throw new Error('Obsolete media command');
      if (
        page.groups.some(
          (group) =>
            group.id === receipt.collectionId &&
            group.collectionRevision === receipt.collectionRevision,
        )
      )
        return;
      cursor = page.page.nextCursor;
    }
    if (cursor && seen.has(cursor)) throw new Error('Repeated media cursor');
    if (cursor) seen.add(cursor);
  } while (cursor);
  throw new Error('Unconfirmed media receipt');
}

export function MediaWriteForm({
  runtime,
  generation,
  collectionId,
  revision,
  propertyId,
  target,
  onSaved,
}: {
  runtime: PropertyRuntime;
  generation: number;
  collectionId: string;
  revision: number | null;
  propertyId: string;
  target: MediaTarget;
  onSaved: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const owner = useMediaWriteOwner();
  const { attempt, createAttempt } = owner;
  const { mounted, track } = useMediaScope(owner);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const allowed = canManageMedia(runtime, generation);
  const submit = async (next: Attempt) => {
    if (!canManageMedia(runtime, generation) || busy) return;
    const lease = owner.claim(next);
    if (!lease) return;
    track(lease);
    let received = false;
    const originalActor = next.actorId;
    const current = () => mounted() && owner.current(lease) && canManageMedia(runtime, generation);
    setBusy(true);
    setMessage('');
    try {
      const actorId = await verifyMediaActor(runtime, generation);
      if (!current()) return;
      if (!actorId || !owner.bind(lease, actorId)) {
        setMessage('현재 수행자를 확인할 수 없어 등록을 진행할 수 없습니다.');
        return;
      }
      lease.sent = true;
      const receipt =
        next.kind === 'file'
          ? await uploadManagedImage(runtime.client, next.value)
          : await createMediaUrl(runtime.client, next.collectionId, next.value);
      received = true;
      if (!current()) return;
      owner.checking(lease);
      await confirmMediaWrite(runtime, propertyId, target, receipt, current);
      if (!current()) return;
      owner.clear(lease);
      setFile(null);
      setUrl('');
      setMessage('등록했습니다.');
      onSaved();
    } catch (error) {
      if (!current()) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      if (
        !received &&
        isApiError(error) &&
        (error.status === 422 || error.status === 403 || (error.status === 409 && !originalActor))
      )
        owner.clear(lease);
      setMessage(
        isApiError(error) && error.status === 409
          ? '사진 목록이 바뀌었습니다. 다시 조회해 주세요.'
          : isApiError(error) && error.status === 422
            ? '사진 정보를 확인해 주세요.'
            : isApiError(error) && error.status === 403
              ? '사진 등록 권한을 확인해 주세요.'
              : '결과를 확인하지 못했습니다. 같은 요청을 다시 시도해 주세요.',
      );
    } finally {
      owner.release(lease);
      if (mounted()) setBusy(false);
    }
  };
  if (!allowed) return null;
  if (owner.editAttempt) return <p role="status">사진 편집 결과를 먼저 확인해 주세요.</p>;
  if (createAttempt)
    return (
      <p role={owner.progress ? 'status' : undefined} aria-busy={!!owner.progress}>
        {owner.progress || '이전 사진 그룹의 등록 결과를 먼저 확인해 주세요.'}
      </p>
    );
  if (
    attempt &&
    (attempt.kind === 'file'
      ? attempt.value.collectionId !== collectionId
      : attempt.collectionId !== collectionId)
  )
    return (
      <p role={owner.progress ? 'status' : undefined} aria-busy={!!owner.progress}>
        {owner.progress || '이전 그룹의 등록 결과를 먼저 확인해 주세요.'}
      </p>
    );
  if (revision === null || revision < 1)
    return attempt ? (
      <div className="grid gap-2" aria-busy={busy || !!owner.progress}>
        {owner.progress && <p role="status">{owner.progress}</p>}
        <p>이전 등록 결과를 확인해 주세요.</p>
        <Button
          variant="outline"
          disabled={busy || !!owner.progress}
          onClick={() => void submit(attempt)}
        >
          같은 요청 재시도
        </Button>
        {message ? <p role="status">{message}</p> : null}
      </div>
    ) : (
      <p>이 그룹은 현재 사진을 등록할 수 없습니다.</p>
    );
  return (
    <div className="grid gap-3 rounded-md border p-3" aria-busy={busy || !!owner.progress}>
      {owner.progress && <p role="status">{owner.progress}</p>}
      <h3 className="font-semibold">사진 등록</h3>
      <label className="grid min-w-0 gap-2">
        사진 파일 (JPG, PNG, WebP)
        <input
          type="file"
          className="file:bg-muted body w-full min-w-0 file:mr-3 file:rounded-md file:border file:px-3 file:py-2"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy || !!attempt}
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
          }}
        />
      </label>
      {file &&
      !attempt &&
      (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
        file.size < 1 ||
        file.size > 10_000_000) ? (
        <p role="alert">10 MB 이하의 JPG, PNG, WebP 파일을 선택해 주세요.</p>
      ) : null}
      <Button
        disabled={
          busy ||
          !!attempt ||
          !file ||
          !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
          file.size < 1 ||
          file.size > 10_000_000
        }
        onClick={() =>
          file &&
          void submit({
            kind: 'file',
            value: {
              commandId: crypto.randomUUID(),
              collectionId,
              expectedCollectionRevision: revision,
              file,
            },
          })
        }
      >
        파일 등록
      </Button>
      <label>
        사진 주소 (HTTPS)
        <input
          type="url"
          className="w-full rounded-md border p-2"
          value={url}
          disabled={busy || !!attempt}
          onChange={(event) => {
            setUrl(event.target.value);
          }}
        />
      </label>
      {url && !safeImageUri(url) ? (
        <p role="alert">주소에 ?, #, 계정 정보가 없는 HTTPS 사진 주소를 입력해 주세요.</p>
      ) : null}
      <Button
        disabled={busy || !!attempt || !safeImageUri(url)}
        onClick={() => {
          const primaryUri = safeImageUri(url);
          if (!primaryUri) return;
          void submit({
            kind: 'url',
            collectionId,
            value: {
              command_id: crypto.randomUUID(),
              expected_collection_revision: revision,
              title: null,
              primary_uri: primaryUri,
              preview_uri: null,
            },
          });
        }}
      >
        주소 등록
      </Button>
      {attempt && !busy && !owner.progress ? (
        <Button variant="outline" onClick={() => void submit(attempt)}>
          같은 요청 재시도
        </Button>
      ) : null}
      {message ? <p role="status">{message}</p> : null}
    </div>
  );
}
